import { useEffect, useRef, useState } from "react";
import { Users, Crosshair, Heart, RotateCcw, ChevronRight, Coins } from "lucide-react";
import { RoadEngine } from "./roadEngine";
import { SpaceHomeBtn } from "./SpaceNav";
import { sfx } from "./audio";
import { spaceApi } from "./spaceApi";

const R = "/game/road";
const B = "/game/bunker";
const BASE_BGM = "/assets/vault/audio/command_deck_theme.mp3";
const HERO_SPRITES = { jess: `${R}/hero_jess.png`, nancy: `${R}/hero_nancy.png`, blaze: `${R}/hero_blaze.png` };

// Stages 4+: repeatable escalating bunker-corridor sweep. Reuses the surfaceSweep
// engine mode (no vehicle), themed for the underground. Win → advance + scrap.
export default function CorridorRun({ stage, roster, reward, onWin, onExit }) {
  const st = stage || 4;
  const isBoss = st % 5 === 0;
  const canvasRef = useRef(null);
  const engineRef = useRef(null);
  const [phase, setPhase] = useState("playing");
  const [hud, setHud] = useState({ troops: 0, weaponTier: 2, lives: 3, stage: 1, progress: 0 });
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    setPhase("playing");
    setHud({ troops: 0, weaponTier: 2, lives: 3, stage: 1, progress: 0 });
    const eng = new RoadEngine(canvasRef.current, {
      campaign: true,
      surfaceSweep: true,
      initialRoster: roster,
      enableRescue: false,
      startWeapon: 2,
      troopShield: true,
      healPulse: true,
      stageLen: 620 + st * 30,
      sweepBoss: isBoss,
      bossName: "CORRIDOR WARLORD", bossHp: 40 + st * 6, bossSprite: `${B}/boss_warlord.png`,
      heroSprites: HERO_SPRITES, stageBackdrops: { 1: `${B}/bg_bunker.jpg` },
      onUpdate: (s) => setHud(s),
      onFinish: () => { sfx.victory(); setPhase("victory"); },
      onGameOver: () => { sfx.defeat(); setPhase("gameover"); },
    });
    engineRef.current = eng;
    eng.mount();
    spaceApi.gameAssets().then((a) => sfx.startBgm(a.combat_theme || a.mist_theme || a.road_patrol_theme)).catch(() => {});
    const onResize = () => eng.resize();
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); eng.unmount(); sfx.startBgm(BASE_BGM); };
  }, [runId, st, roster]);

  return (
    <div className="ss-viewport rp-viewport" data-testid="corridor-run">
      <SpaceHomeBtn onClick={onExit} testid="corridor-run-exit" />
      <canvas ref={canvasRef} className="ss-canvas" data-testid="corridor-run-canvas" />

      <div className="rp-hud" data-testid="corridor-run-hud">
        <div className="rp-stat"><b>ST {st}</b><span>Corridor Sector</span></div>
        <div className="rp-stat"><Users size={14} /> <b>{hud.troops}</b><span>buffer</span></div>
        <div className="rp-stat wpn"><Crosshair size={14} /> T<b>{hud.weaponTier}</b></div>
        <div className="rp-lives">
          {[0, 1, 2].map((i) => <Heart key={i} size={15} fill={i < hud.lives ? "#ff4560" : "none"} stroke={i < hud.lives ? "#ff4560" : "#5b6b82"} />)}
        </div>
        <div className="rp-progress"><span style={{ width: `${hud.progress * 100}%` }} /></div>
      </div>

      {phase === "playing" && <div className="rp-hint" data-testid="corridor-run-hint">{isBoss ? `⚠ BOSS SECTOR ${st} · survive to the Warlord and end it` : `Drag to steer · clear the corridor to secure Stage ${st}`}</div>}

      {phase === "victory" && (
        <div className="ss-overlay" data-testid="corridor-run-victory" style={{ background: "rgba(6,8,14,0.9)" }}>
          <span className="ov-t" style={{ color: "#8affc9" }}>SECTOR {st} SECURED</span>
          <span className="ov-s">The corridor is clear. Salvage recovered:</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#ffe08a", fontWeight: 900, fontSize: 22, margin: "10px 0" }}>
            <Coins size={20} /> +{reward} Scrap
          </div>
          <button className="ss-enter-btn" data-testid="corridor-run-claim" onClick={onWin} style={{ borderColor: "rgba(55,224,160,0.7)", background: "rgba(55,224,160,0.16)", color: "#8affc9" }}>
            Claim &amp; Return <ChevronRight size={16} />
          </button>
        </div>
      )}

      {phase === "gameover" && (
        <div className="ss-overlay lose" data-testid="corridor-run-gameover">
          <span className="ov-t">Corridor Overrun</span>
          <span className="ov-s">The swarm pushed you back. Regroup and descend again.</span>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="ss-enter-btn" data-testid="corridor-run-retry" onClick={() => setRunId((n) => n + 1)}><RotateCcw size={16} /> Retry</button>
            <button className="ss-enter-btn" data-testid="corridor-run-abort" onClick={onExit} style={{ opacity: 0.85 }}>Return to Hub</button>
          </div>
        </div>
      )}
    </div>
  );
}
